import { createSlice } from '@reduxjs/toolkit';
import { calendarApiV4 as api } from 'api';
import { transformFiltersToQuery } from '../utils/transformFilterToQuery';

export const serviceSlice = createSlice({
    name: 'serviceV4',
    initialState: {
        count: 0,
        total: 0,
        pending: false,
        savePending: false,
        services: [],
        currentPage: 1,
        totalPages: 1,
        error: null,
        deletePending: false,
        deleteError: null,
        deleteId: null,
        issueStats: [],
        issueStatsPending: false,
        affectedTotal: 0,
        lastServiceChangeAt: null,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        setLastServiceChangeAt: (state, action) => {
            state.lastServiceChangeAt = action.payload;
        },
        startIssueStats: (state) => {
            state.issueStatsPending = true;
        },
        stopIssueStats: (state) => {
            state.issueStatsPending = false;
        },
        setIssueStats: (state, action) => {
            state.issueStatsPending = false;
            // Endpoint returns { issues: [{ code, title, icon, serviceCount }],
            // totalAffectedServices: number }.
            const payload = action.payload || {};
            state.issueStats = Array.isArray(payload.issues) ? payload.issues : [];
            state.affectedTotal = payload.totalAffectedServices ?? 0;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        startDelete: (state, action) => {
            state.deletePending = true;
            state.deleteId = action.payload;
        },
        stopDelete: (state, action) => {
            state.deletePending = false;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        addService: (state, action) => {
            state.savePending = false;
            state.services = [action.payload, ...state.services];
        },
        editService: (state, action) => {
            state.savePending = false;
            const index = state.services.findIndex((service) => service.id === action.payload.id);
            if (index !== -1) {
                state.services = [
                    ...state.services.slice(0, index),
                    { ...state.services[index], ...action.payload },
                    ...state.services.slice(index + 1),
                ];
            }
        },
        deleteService: (state, action) => {
            state.deletePending = false;
            const index = state.services.findIndex((service) => service.id === action.payload.id);
            if (index !== -1) {
                state.services = [...state.services.slice(0, index), ...state.services.slice(index + 1)];
            }
        },
        setServices: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
            state.services = action.payload.items;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
        setScheduledCalendarsIds: (state, action) => {
            state.pending = false;
            state.scheduledCalendarsIds = action.payload;
        },
        setCapacities: (state, action) => {
            state.capacities = action.payload.reduce((acc, item) => {
                acc[item.templateId] = { totalCapacity: item.totalCapacity, occupancy: item.occupancy };
                return acc;
            }, {});
        },
    },
});

const {
    start,
    stop,
    setServices,
    stopDelete,
    setError,
    addService,
    editService,
    deleteService,
    startDelete,
    startSave,
    stopSave,
    setSaveError,
    startIssueStats,
    stopIssueStats,
    setIssueStats,
    setLastServiceChangeAt,
    // setScheduledCalendarsIds,
    // setCapacities,
} = serviceSlice.actions;

export const getServices = (state) => state.serviceV4.services;
export const getServicesCount = (state) => state.serviceV4.count;
export const getServicesTotal = (state) => state.serviceV4.total;
export const getServicesCurrentPage = (state) => state.serviceV4.currentPage;
export const getServicesTotalPages = (state) => state.serviceV4.totalPages;
export const getServicesPending = (state) => state.serviceV4.pending;
export const getServicesSavePending = (state) => state.serviceV4.savePending;
export const getServiceDeletePending = (state) => state.serviceV4.deletePending;
export const getServiceDeleteId = (state) => state.serviceV4.deleteId;
export const getServicesError = (state) => state.serviceV4.error;
export const getScheduledCalendarsIds = (state) => state.serviceV4.scheduledCalendarsIds;
export const getCapacities = (state) => state.serviceV4.capacities;
export const getServiceIssueStats = (state) => state.serviceV4.issueStats;
export const getServiceIssueStatsPending = (state) => state.serviceV4.issueStatsPending;
export const getAffectedServicesTotal = (state) => state.serviceV4.affectedTotal;
export const getLastServiceChangeAt = (state) => state.serviceV4.lastServiceChangeAt;

// Record the moment a service was changed so the UI can warn that issue stats
// take a few minutes to refresh.
export const markServiceChangedAction = () => (dispatch) => {
    dispatch(setLastServiceChangeAt(Date.now()));
};

export const resetServicesAction = () => async (dispatch) => {
    dispatch(setServices({ count: 0, total: 0, currentPage: 1, totalPages: 1, items: [] }));
    dispatch(start());
};

export const fetchServicesAction =
    ({ queryParams, onSuccess, onFailure, getResponse } = {}) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(queryParams);
            dispatch(start());
            api.get('services' + query)
                .then((response) => {
                    if (getResponse) {
                        getResponse(response.data);
                    } else {
                        dispatch(setServices(response.data));
                    }
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stop());
            return console.error(e.message);
        }
    };

// Default status for the issue stats (mirrors the "active" filter default).
const DEFAULT_ISSUE_STATS_PARAMS = { status: ['active', 'future'] };

export const fetchServiceIssueStatsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            const query = transformFiltersToQuery({ ...DEFAULT_ISSUE_STATS_PARAMS, ...queryParams });
            dispatch(startIssueStats());
            api.get('services/stats/issues' + query)
                .then((response) => {
                    dispatch(setIssueStats(response.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(stopIssueStats());
                    dispatch(setError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopIssueStats());
            return console.error(e.message);
        }
    };

export const recalculateServiceIssuesAction =
    ({ organizationId, serviceId, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            api.post('services/issues/recalculate', {
                organizationId,
                ...(serviceId ? { serviceId } : {}),
            })
                .then((response) => {
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    onFailure?.(error);
                });
        } catch (e) {
            onFailure?.(e);
            return console.error(e.message);
        }
    };

export const fetchServicePreviewIssuesAction =
    ({ id, data, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            api.post('services/preview-issues', {
                id,
                data,
            })
                .then((response) => {
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    onFailure?.(error);
                });
        } catch (e) {
            onFailure?.(e);
            return console.error(e.message);
        }
    };

export const getServicesAction =
    ({ id, queryParams, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(queryParams);
            api.get('services/' + id + query)
                .then((response) => {
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            onFailure?.(e);
            return console.error(e.message);
        }
    };

export const addServiceAction =
    ({ serviceDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            api.post('services', serviceDto)
                .then((response) => {
                    dispatch(addService(response?.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error(e.message);
        }
    };

export const editServiceAction =
    ({ id, serviceDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            api.put('services/' + id, serviceDto)
                .then((response) => {
                    dispatch(editService(response.data));
                    onSuccess?.(response.data);
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    onFailure?.(error);
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error('error', e);
        }
    };

export const deleteServiceAction =
    ({ id, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startDelete(id));
            api.delete('services/' + id)
                .then((response) => {
                    dispatch(deleteService({ id }));
                    onSuccess?.();
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    onFailure?.();
                });
        } catch (e) {
            dispatch(stopDelete());
            return console.error('error', e);
        }
    };

export default serviceSlice.reducer;
